/* eslint-disable max-lines */
// TODO: The file should be refactored to decrease lines
import {AxiosPromise} from 'axios';
import Store from 'store';

import {CATEGORY_PRODUCT_LIMIT} from 'constants/category';
import {CATEGORY} from 'constants/actions';
import {ENDPOINTS} from 'constants/endpoints';
import {API_URL} from 'constants/api';

import {ActionType, EmptyActionType} from '@typescript/common/actionTypes';
import {FilterItemType, UrlParamsType} from '@typescript/common/categoryTypes';
import {FilterActionType, ActiveFilterType} from 'reducers/category/filters/filtersType';
import {ActiveFiltersType} from 'reducers/category/activeFilters/activeFiltersType';
import {FetchCategoryMsResponseType} from 'reducers/category/categoryItemsType';
import {createUrlString} from 'libs/url.js';
import {instanceGusaBackend} from 'libs/api';
import {
    generateActionForCategoryFetch,
    transformFiltersToSearchString,
} from 'libs/category/category.js';

/**
 * Get current category data
 * @param {number} id Category id
 * @returns {ActionType<AxiosPromise>} Action
 */
export function getCategory(id: number): ActionType<AxiosPromise> {
    return {
        type: CATEGORY.GET_CATEGORY,
        payload: instanceGusaBackend({
            method: 'get',
            url: `${ENDPOINTS.CATEGORY_GET_INFO}/${id}`,
        }),
    };
}

export type ReturnDataCategoryItemType = {
    page: number;
    order?: string | number;
    dir?: string;
    cancelToken?: any;
    append?: any;
    algoliaParams?: {q: string; search_provider?: string} | {};
    otherParams?: Record<string, Array<string>>;
};

export const excludedFieldsFromElastic =
    '&excludedFields=weighted,meta,attributeSetId,bestsellers,customProductBadge,glassesType,insurance,isSaleable,maxPrice,maxSaleQty,relatedProductSku,useLensPackage,visibility';

/**
 * Get current category product item
 *
 * @param {number} id - Category id
 * @param {ActiveFilterType} params - Parameters for catalog filtering and pagination
 * @param {ReturnDataCategoryItemType} data - Data for pagination
 * @returns {ActionType<AxiosPromise>} Action
 */
export function getCategoryItem(
    id: number,
    params: ActiveFilterType,
    data: ReturnDataCategoryItemType,
): ActionType<
    {promise: AxiosPromise<FetchCategoryMsResponseType>; data: {id: number; isAppend: any}},
    {currentlyRequiredPage: string | number}
> {
    // eslint-disable-next-line no-param-reassign
    if (params.p) delete params.p;

    const filter = Object.keys(params)
        .filter(code => ['tab', 'frame'].indexOf(code) === -1 && params[code]?.length)
        // eslint-disable-next-line @typescript-eslint/naming-convention
        .map(code => `${code}=${params[code].map(({value_id}) => value_id).join(',')}`)
        .join('&');

    const savedIDs = Store.get('SEARCH_RESULTS_IDS_LIST');
    const offset = CATEGORY_PRODUCT_LIMIT * (Number(data.page) - 1);
    const urlParams = {
        limit: [CATEGORY_PRODUCT_LIMIT],
        offset: [offset],
        ...(data.order && data.dir
            ? {
                  dir: [data.dir],
                  order: [data.order],
              }
            : {}),
        ...(savedIDs ? {ids: [savedIDs]} : {}),
    };
    const url = `${ENDPOINTS.ELASTIC_CATEGORY}/${id}?${createUrlString(urlParams)}${
        params ? `&${filter}` : ''
    }`;

    return {
        type: CATEGORY.GET_CATEGORY_ITEMS,
        payload: {
            promise: instanceGusaBackend({
                method: 'get',
                baseURL: API_URL,
                url,
                cancelToken: data.cancelToken,
            }),
            data: {
                id,
                isAppend: data.append,
            },
        },
        meta: {currentlyRequiredPage: data.page},
    };
}

/**
 * Get current category count item
 *
 * @param {number} id - Category id
 * @param {UrlParamsType} params - Parameters for catalog filtering and pagination
 * @param {ReturnDataCategoryItemType} data - Data for pagination
 * @returns {ActionType<AxiosPromise>} Action
 */
export function getCategoryItemCount(
    id: number,
    params: UrlParamsType,
    data: ReturnDataCategoryItemType,
): ActionType<AxiosPromise> {
    // eslint-disable-next-line no-param-reassign
    if (params.p) delete params.p;
    const filter = Object.keys(params)
        .map(f =>
            Object.keys(params[f])
                .map(item => `filter[${f}][]=${params[f][item].value}`)
                .join('&'),
        )
        .join('&');

    const savedIDs = Store.get('SEARCH_RESULTS_IDS_LIST');

    return {
        type: CATEGORY.GET_CATEGORY_ITEMS_COUNT,
        payload: instanceGusaBackend({
            method: 'get',
            url: `${ENDPOINTS.CATEGORY_GET_COUNT}?with_upsell=1&category_id=${id}&limit=15&page=${
                data.page
            }${data.order && data.dir ? `&dir=${data.dir}&order=${data.order}` : ''}${
                params ? `&${filter}` : ''
            }${savedIDs ? `&ids=${savedIDs}` : ''}`,
            cancelToken: data.cancelToken,
            params: {eligibleForHto: true},
        }),
    };
}

/**
 * Get filter for category
 * @param {number} id - category id
 * @param {string} query - search query
 * @returns {ActionType<AxiosPromise>} Action
 */
export function getFilters(id: number, query?: string): ActionType<AxiosPromise> {
    return {
        type: CATEGORY.GET_FILTERS_CATEGORY,
        payload: instanceGusaBackend({
            method: 'get',
            url: `${ENDPOINTS.ELASTIC_CATEGORY_FILTERS}/${id}`,
            baseURL: API_URL,
            params: {
                search_provider: 'algolia',
                ...(query ? {q: query} : {}),
            },
        }),
    };
}

/**
 * Get current category product item
 *
 * @param {number} id - Category id
 * @param {ActiveFiltersType} params - Parameters for catalog filtering and pagination
 * @param {ReturnDataCategoryItemType} data - Data for pagination
 * @returns {ActionType<AxiosPromise>} Action
 */
export function fetchCategoryItems(
    id: number,
    params: ActiveFiltersType,
    data: ReturnDataCategoryItemType,
): ActionType<
    {promise: AxiosPromise<FetchCategoryMsResponseType>; data: {id: number; isAppend: boolean}},
    {currentlyRequiredPage: string | number}
> {
    const url = `${ENDPOINTS.ELASTIC_CATEGORY}/${id}?${transformFiltersToSearchString(
        params,
        data,
    )}${excludedFieldsFromElastic}`;

    return generateActionForCategoryFetch(id, url, data);
}

/**
 * Set active page number
 * @param {number} pageNumber - New page number
 * @returns {ActionType<number>} Action
 */
export const setPageNumber = (pageNumber: number): ActionType<number> => ({
    type: CATEGORY.SET_PAGE_NUMBER,
    payload: pageNumber,
});

/**
 * Set active filters
 * @param {ActiveFiltersType} filters - New active filters
 * @returns {ActionType<ActiveFiltersType>} Action
 */
export function setActiveFilters(filters: ActiveFiltersType): ActionType<ActiveFiltersType> {
    return {
        type: CATEGORY.SET_ACTIVE_FILTERS,
        payload: filters,
    };
}

/**
 * Set active filers for eyeWear filter
 * @param {'eyeglasses | sunglasses'} filter filter
 * @returns {ActionType<ActiveFiltersType>} Action
 */
export function setEyeWearFilter(filter) {
    return {
        type: CATEGORY.SET_EYE_WEAR_FILTER,
        payload: filter,
    };
}

/**
 * Get active sorting
 * @param {{}} sort Current number
 * @returns {{sort: {}, type: string}} Action
 */
export function getActiveSort(sort: {}): {sort: {}; type: string} {
    return {
        type: CATEGORY.GET_ACTIVE_SORT,
        sort,
    };
}

/**
 * Remove filter
 * @returns {EmptyActionType} Action
 */
export function removeActiveFilter(): EmptyActionType {
    return {
        type: CATEGORY.REMOVE_ACTIVE_FILTERS,
    };
}

/**
 * Get state (show\hide) filters
 * @param {boolean} state  - show\hide filters or sort
 * @returns {{type: string, state: boolean}} Action
 */
export function stateFilter(state: boolean): {state: boolean; type: string} {
    return {
        type: CATEGORY.GET_STATE_FILTER,
        state,
    };
}

/**
 * Get status apply filters
 * @param {boolean} state - status
 * @returns {{type: string, state: boolean}} Action
 */
export function applyFilter(state: boolean): {state: boolean; type: string} {
    return {
        type: CATEGORY.GET_APPLY_FILTER,
        state,
    };
}

/**
 * Get state (show\hide) sort
 * @param {boolean} state  - show\hide filters or sort
 * @returns {{type: string, state: boolean}} Action
 */
export function stateSort(state: boolean): {state: boolean; type: string} {
    return {
        type: CATEGORY.GET_STATE_SORT,
        state,
    };
}

/**
 * Set data about current selected filters
 * @param {string} codeFilter - Code filter
 * @param {FilterItemType} item - Selected filter item
 * @returns {ActionType<{filterType: string, filterStringValue: string}>} Action
 */
export function changedFilter(
    codeFilter: string,
    item: FilterItemType,
): ActionType<FilterActionType> {
    return {
        type: CATEGORY.CHANGED_FILTER,
        payload: {codeFilter, item, value: item.value},
    };
}

/**
 * Removes any data from category reducer
 * @returns {EmptyActionType} - Returned action
 */
export const clearCategoryData = (): EmptyActionType => ({
    type: CATEGORY.CLEAR_CATEGORY_DATA,
});
